<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import { setNewPassword } from '../../../api/auth';
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

export default {
  components: { Spinner, Icon },
  props: {
    resetPasswordToken: { type: String, default: '' },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: {
        confirmPassword: '',
        password: '',
      },
      newPasswordAPI: {
        message: '',
        showLoading: false,
      },
      error: '',
      showPassword: false,
      showConfirmPassword: false,
    };
  },
  validations: {
    credentials: {
      password: {
        required,
        minLength: minLength(6),
      },
      confirmPassword: {
        required,
        minLength: minLength(6),
        isEqPassword(value) {
          if (value !== this.credentials.password) {
            return false;
          }
          return true;
        },
      },
    },
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    loginBackgroundImage() {
      return this.globalConfig.loginBackgroundImage || null;
    },
    loginOverlayColor() {
      return this.globalConfig.loginOverlayColor || '#1f93ff';
    },
    loginOverlayOpacity() {
      const opacity = this.globalConfig.loginOverlayOpacity;
      return typeof opacity === 'number' ? opacity : 80;
    },
    backgroundStyle() {
      const defaultImage =
        'https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=2574&auto=format&fit=crop';
      const image = this.loginBackgroundImage || defaultImage;
      return {
        backgroundImage: `url('${image}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    },
    overlayStyle() {
      const hex = this.loginOverlayColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const opacity = this.loginOverlayOpacity / 100;

      return {
        background: `linear-gradient(to top, rgba(${r}, ${g}, ${b}, ${opacity * 0.9}) 0%, rgba(${r}, ${g}, ${b}, ${opacity * 0.5}) 50%, transparent 100%)`,
      };
    },
    brandColor() {
      return this.globalConfig.brandPrimaryColor || '#1f93ff';
    },
    buttonGradientStyle() {
      const hex = this.brandColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const lighterR = Math.min(255, r + 40);
      const lighterG = Math.min(255, g + 40);
      const lighterB = Math.min(255, b + 40);
      const darkerR = Math.max(0, r - 30);
      const darkerG = Math.max(0, g - 30);
      const darkerB = Math.max(0, b - 30);

      return {
        background: `linear-gradient(135deg, rgb(${lighterR}, ${lighterG}, ${lighterB}) 0%, rgb(${r}, ${g}, ${b}) 50%, rgb(${darkerR}, ${darkerG}, ${darkerB}) 100%)`,
      };
    },
  },
  mounted() {
    if (!this.resetPasswordToken) {
      window.location = DEFAULT_REDIRECT_URL;
    }
  },
  methods: {
    showAlertMessage(message) {
      this.newPasswordAPI.showLoading = false;
      useAlert(message);
    },
    submitForm() {
      this.newPasswordAPI.showLoading = true;
      const credentials = {
        confirmPassword: this.credentials.confirmPassword,
        password: this.credentials.password,
        resetPasswordToken: this.resetPasswordToken,
      };
      setNewPassword(credentials)
        .then(() => {
          window.location = DEFAULT_REDIRECT_URL;
        })
        .catch(error => {
          this.showAlertMessage(
            error?.message || this.$t('SET_NEW_PASSWORD.API.ERROR_MESSAGE')
          );
        });
    },
  },
};
</script>

<template>
  <main class="relative min-h-screen w-full overflow-hidden">
    <!-- Fullscreen Background Image -->
    <div class="absolute inset-0" :style="backgroundStyle" />

    <!-- Configurable Overlay -->
    <div class="absolute inset-0" :style="overlayStyle" />

    <!-- Floating geometric shapes -->
    <div
      class="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse hidden lg:block"
    />
    <div
      class="absolute bottom-20 right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000 hidden lg:block"
    />

    <!-- Main Content Container -->
    <div
      class="relative z-10 min-h-screen flex items-center justify-center py-8 px-4"
    >
      <!-- Card -->
      <div
        class="w-full max-w-md bg-white/40 backdrop-blur-xl rounded-3xl border border-black/10 shadow-2xl p-8"
      >
        <!-- Logo -->
        <div class="mb-6 flex justify-center">
          <img
            :src="globalConfig.logo || '/logo.png'"
            :alt="globalConfig.installationName"
            class="w-auto h-10"
          />
        </div>

        <!-- Title -->
        <div class="mb-6 text-center">
          <h1 class="text-xl font-bold text-slate-900 mb-1">
            {{ $t('SET_NEW_PASSWORD.TITLE') }}
          </h1>
          <p class="text-sm text-slate-600">
            Escolha uma senha forte para proteger sua conta
          </p>
        </div>

        <!-- Form -->
        <form class="space-y-4" @submit.prevent="submitForm">
          <!-- Password Field -->
          <div class="space-y-1">
            <label class="block text-sm font-medium text-slate-700">
              {{ $t('SET_NEW_PASSWORD.PASSWORD.LABEL') }}
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <Icon icon="i-lucide-lock" class="size-4 text-slate-400" />
              </div>
              <input
                v-model="credentials.password"
                :type="showPassword ? 'text' : 'password'"
                name="password"
                data-testid="password_input"
                required
                :placeholder="$t('SET_NEW_PASSWORD.PASSWORD.PLACEHOLDER')"
                class="w-full pl-10 pr-10 py-3 bg-white/60 border border-black/10 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                :class="{
                  'ring-2 ring-red-500/50': v$.credentials.password.$error,
                }"
                @blur="v$.credentials.password.$touch"
              />
              <button
                type="button"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
                @click="showPassword = !showPassword"
              >
                <Icon
                  :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                  class="size-4 text-slate-400 hover:text-slate-600"
                />
              </button>
            </div>
            <p
              v-if="v$.credentials.password.$error"
              class="mt-1 text-xs text-red-600"
            >
              {{ $t('SET_NEW_PASSWORD.PASSWORD.ERROR') }}
            </p>
          </div>

          <!-- Confirm Password Field -->
          <div class="space-y-1">
            <label class="block text-sm font-medium text-slate-700">
              {{ $t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.LABEL') }}
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <Icon
                  icon="i-lucide-lock-keyhole"
                  class="size-4 text-slate-400"
                />
              </div>
              <input
                v-model="credentials.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                name="confirm_password"
                data-testid="confirm_password_input"
                required
                :placeholder="
                  $t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.PLACEHOLDER')
                "
                class="w-full pl-10 pr-10 py-3 bg-white/60 border border-black/10 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                :class="{
                  'ring-2 ring-red-500/50':
                    v$.credentials.confirmPassword.$error,
                }"
                @blur="v$.credentials.confirmPassword.$touch"
              />
              <button
                type="button"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <Icon
                  :icon="
                    showConfirmPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'
                  "
                  class="size-4 text-slate-400 hover:text-slate-600"
                />
              </button>
            </div>
            <p
              v-if="v$.credentials.confirmPassword.$error"
              class="mt-1 text-xs text-red-600"
            >
              {{ $t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.ERROR') }}
            </p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            data-testid="submit_button"
            :disabled="
              v$.credentials.password.$invalid ||
              v$.credentials.confirmPassword.$invalid ||
              newPasswordAPI.showLoading
            "
            class="w-full py-3 mt-2 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            :style="buttonGradientStyle"
          >
            <span
              v-if="newPasswordAPI.showLoading"
              class="flex items-center justify-center gap-2"
            >
              <Spinner size="small" />
              <span>{{ $t('SET_NEW_PASSWORD.SUBMIT') }}</span>
            </span>
            <span v-else>{{ $t('SET_NEW_PASSWORD.SUBMIT') }}</span>
          </button>
        </form>

        <!-- Password Requirements -->
        <div class="mt-6 pt-5 border-t border-black/10">
          <p class="text-xs text-slate-500 mb-2">Requisitos da senha:</p>
          <ul class="space-y-1 text-xs text-slate-600">
            <li class="flex items-center gap-2">
              <Icon
                :icon="
                  credentials.password.length >= 6
                    ? 'i-lucide-check-circle'
                    : 'i-lucide-circle'
                "
                :class="
                  credentials.password.length >= 6
                    ? 'text-green-600'
                    : 'text-slate-300'
                "
                class="size-3"
              />
              Minimo 6 caracteres
            </li>
            <li class="flex items-center gap-2">
              <Icon
                :icon="
                  credentials.password === credentials.confirmPassword &&
                  credentials.confirmPassword.length > 0
                    ? 'i-lucide-check-circle'
                    : 'i-lucide-circle'
                "
                :class="
                  credentials.password === credentials.confirmPassword &&
                  credentials.confirmPassword.length > 0
                    ? 'text-green-600'
                    : 'text-slate-300'
                "
                class="size-3"
              />
              Senhas coincidem
            </li>
          </ul>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-text-fill-color: #1e293b;
  -webkit-box-shadow: 0 0 0px 1000px rgba(255, 255, 255, 0.6) inset;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
